import { PageTabBar } from '@/components/app/page-tab-bar';
import type { ConversationTabCounts } from '@/features/conversations/api';
import { cn } from '@/lib/utils';

export const CONVERSATION_TAB_VALUES = [
  'from_ads',
  'needs_reply',
  'in_conversation',
  'cold',
  'all',
] as const;

export type ConversationTabValue = (typeof CONVERSATION_TAB_VALUES)[number];

/** The view the inbox opens on when the URL names none. */
export const DEFAULT_CONVERSATION_TAB: ConversationTabValue = 'from_ads';

const TABS: { value: ConversationTabValue; label: string }[] = [
  // The inbox opens here: every thread that came from an ad.
  { value: 'from_ads', label: 'From Ads' },
  { value: 'needs_reply', label: 'Needs Reply' },
  { value: 'in_conversation', label: 'In Conversation' },
  { value: 'cold', label: 'Cold' },
  { value: 'all', label: 'All' },
];

/**
 * The inbox's four views. `Needs Reply` carries the count in the attention
 * colour so "who is waiting on us" reads at a glance; the others are neutral.
 *
 * `pills` is the phone's compact chip strip. `bar` is the desktop page's
 * full-width strip across the top of the inbox: text tabs, the active one on
 * a tinted ground, counts as badges.
 */
export function ConversationTabs({
  value,
  onChange,
  counts,
  className,
  variant = 'pills',
}: {
  value: ConversationTabValue;
  onChange: (value: ConversationTabValue) => void;
  counts: ConversationTabCounts | null;
  className?: string;
  variant?: 'pills' | 'bar';
}) {
  if (variant === 'bar') {
    return (
      <PageTabBar
        label="Conversation views"
        className={className}
        value={value}
        onChange={onChange}
        tabs={TABS.map((tab) => ({
          ...tab,
          count: counts?.[tab.value],
          urgent: tab.value === 'needs_reply',
        }))}
      />
    );
  }

  return (
    <div
      role="tablist"
      aria-label="Conversation views"
      className={cn(
        'flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {TABS.map((tab) => {
        const selected = tab.value === value;
        const count = counts?.[tab.value];
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.value)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] whitespace-nowrap transition-colors',
              selected
                ? 'bg-foreground font-semibold text-background'
                : 'bg-muted font-medium text-foreground hover:bg-muted/70'
            )}
          >
            {tab.label}
            {count !== undefined ? (
              <span
                className={cn(
                  'tabular-nums',
                  selected
                    ? 'opacity-80'
                    : tab.value === 'needs_reply' && count > 0
                      ? 'font-bold text-primary'
                      : 'text-muted-foreground'
                )}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
